import {
  TextEditor,
  SelectColumn,
  Column,
  FormatterProps,
} from 'react-data-grid';
import User from '../../model/User';
import SelectEditor from './SelectEditor';
import Tooltip from '@reach/tooltip';
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSortDown} from '@fortawesome/free-solid-svg-icons';
import {isNil} from 'ramda';
import EmployeeNumberPrefixInfo from './components/employee-number-prefix-info';
import EmployeeTypeInfo from './components/employee-type-info';
import DateFormatInfo from './components/date-format-info';
import { JobTitleData } from '../job-title-setting';
import InfoTip, {P} from '@gakki/components/info-tip/info-tip';

const gender = [
  {code: '0', name: '男'},
  {code: '1', name: '女'},
];

type CellFormatProps = {
  children?: React.ReactNode;
  displayText?: React.ReactNode | string;
  errorMessage?: string;
  maxWidth?: string;
} & FormatterProps;
function autoFocusAndSelect(input: HTMLInputElement | null) {
  input?.focus();
  input?.select();
}

function CellFormat(props: CellFormatProps) {
  const {row, column, displayText, errorMessage, maxWidth, rowIdx} = props;
  const error = errorMessage || (row.errors && row.errors[column.key]);
  const text = displayText || row[column.key];
  return (
    <div
      data-test-id={`${rowIdx}-${column.key}`}
      className={
        'relative h-full w-full overflow-hidden text-ellipsis whitespace-nowrap'
      }
      style={{
        maxWidth: maxWidth ? maxWidth : 'none',
      }}
    >
      {error && (
        <>
          <Tooltip label={error}>
            <div className={'w-full h-full text-[#d10b0b]'}>
              <span>{text}</span>
            </div>
          </Tooltip>
          {(text === '' || isNil(text)) && (
            <div className={'absolute bottom-0 right-0 z-[-1]'}>
              <FontAwesomeIcon
                icon={['fal', 'exclamation-triangle']}
                className={'text-[10px] text-[#d10b0b]'}
              />
            </div>
          )}
        </>
      )}
      {!error && text}
    </div>
  );
}

function SelectCellFormat(props: CellFormatProps) {
  return (
    <div className={'flex justify-between items-center h-full relative'}>
      <CellFormat {...props} maxWidth={'calc(100% - 8px)'} />
      <FontAwesomeIcon icon={faSortDown} />
    </div>
  );
}

type Option = {
  name: string;
  abbr: string;
};
export type Parameters = {
  idType: Option[];
  identityCategory: Option[];
  nationality: Option[];
  maritalStatus: Option[];
};

type CustomColumn<T> = Column<T> & {
  type?: 'option' | 'text' | 'date' | 'numberOption' | 'sequenceNumber' | 'number';
  options?: {label: string; value: string}[];
  jobLevelResolver?: (jobGrade: string) => {label: string; value: string}[];
};

export default function columnConfig(
  parameters: Parameters = {
    idType: [],
    identityCategory: [],
    nationality: [],
    maritalStatus: [],
  },
  jobTitleData: JobTitleData = {
    jobTitles: [],
    jobGrades: [],
  },
) {
  const ColumnConfig: CustomColumn<User>[] = [
    SelectColumn,
    {
      key: 'id',
      name: '',
      width: 35,
      maxWidth: 35,
      frozen: true,
      formatter: props => (
        <div className={'flex justify-around items-center'}>
          {props.rowIdx + 1}
        </div>
      ),
    },
    {
      key: 'code',
      name: (
        <span>
          工號前置碼
          <span className="text-red">*</span>
          <EmployeeNumberPrefixInfo />
        </span>
      ),
      width: 120,
      maxWidth: 120,
      editor: ({rowHeight, row, column, onRowChange, onClose, rowIdx}) => (
        <input
          ref={autoFocusAndSelect}
          data-test-id={`${rowIdx}-${column.key}`}
          className={
            'px-[6px] border-2 border-[#ccc] focus:border-[#66afe9] outline-none'
          }
          style={{
            height: rowHeight,
            backgroundColor: 'var(--background-color)',
          }}
          maxLength={12}
          value={row.code}
          onChange={event =>
            onRowChange({...row, [column.key]: event.target.value})
          }
          onBlur={() => onClose(true)}
        />
      ),
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'seqNo',
      name: (
        <span>
          工號流水號
          <span className="text-red">*</span>
        </span>
      ),
      type: 'sequenceNumber',
      width: 100,
      maxWidth: 100,
      editor: ({rowHeight, row, column, onRowChange, onClose, rowIdx}) => (
        <input
          ref={autoFocusAndSelect}
          data-test-id={`${rowIdx}-${column.key}`}
          className={
            'px-[6px] border-2 border-[#ccc] focus:border-[#66afe9] outline-none'
          }
          style={{
            backgroundColor: 'var(--background-color)',
            height: rowHeight,
          }}
          maxLength={8}
          value={row.seqNo}
          onChange={event =>
            onRowChange({...row, [column.key]: event.target.value})
          }
          onBlur={() => onClose(true)}
        />
      ),
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'lastName',
      name: (
        <span>
          姓
          <span className="text-red">*</span>
        </span>
      ),
      width: 100,
      maxWidth: 100,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'firstName',
      name: (
        <span>
          名
          <span className="text-red">*</span>
        </span>
      ),
      width: 90,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => (
        <CellFormat {...props} displayText={props.row.firstName} />
      ),
    },
    {
      key: 'gender',
      name: (
        <span>
          性別
          <span className="text-red">*</span>
        </span>
      ),
      width: 100,
      maxWidth: 100,
      type: 'numberOption',
      options: gender.map(c => ({value: c.code.toString(), label: c.name})),
      editor: p => (
        <SelectEditor
          isSearchable={false}
          value={p.row.gender?.toString() || ''}
          onChange={value =>
            p.onRowChange({...p.row, gender: Number(value)}, true)
          }
          options={gender.map(c => ({value: c.code.toString(), label: c.name}))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat
          {...props}
          displayText={
            gender.find(g => g.code === props.row.gender?.toString())?.name ||
            ''
          }
        />
      ),
    },
    {
      key: 'nationality',
      name: (
        <span>
          國籍
          <span className="text-red">*</span>
        </span>
      ),
      width: 100,
      type: 'option',
      options: parameters.nationality.map(c => ({
        value: c.abbr,
        label: c.name,
      })),
      editor: p => (
        <SelectEditor
          isSearchable={false}
          value={p.row.nationality || ''}
          onChange={value =>
            p.onRowChange({...p.row, nationality: value}, true)
          }
          options={parameters.nationality.map(c => ({
            value: c.abbr,
            label: c.name,
          }))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat
          {...props}
          displayText={
            parameters.nationality.find(g => g.abbr === props.row.nationality)
              ?.name || ''
          }
        />
      ),
    },
    {
      key: 'certificateType',
      name: (
        <span>
          證件類別
          <span className="text-red">*</span>
        </span>
      ),
      width: 100,
      type: 'numberOption',
      options: parameters.idType.map(c => ({value: c.abbr, label: c.name})),
      editor: p => (
        <SelectEditor
          isSearchable={false}
          value={p.row.certificateType?.toString() || ''}
          onChange={value =>
            p.onRowChange({...p.row, certificateType: Number(value)}, true)
          }
          options={parameters.idType.map(c => ({value: c.abbr, label: c.name}))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat
          {...props}
          displayText={
            parameters.idType.find(
              g => g.abbr === props.row.certificateType?.toString(),
            )?.name || ''
          }
        />
      ),
    },
    {
      key: 'certificateNo',
      name: (
        <span>
          證件號碼
          <span className="text-red">*</span>
        </span>
      ),
      minWidth: 150,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'birthday',
      name: (
        <div>
          生日
          <span className="text-red">*</span>
          <DateFormatInfo propName={'生日'} />
        </div>
      ),
      width: 120,
      maxWidth: 120,
      type: 'date',
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'onboardingDate',
      name: (
        <div>
          到職日
          <span className="text-red">*</span>
          <DateFormatInfo propName={'到職日'} />
        </div>
      ),
      width: 100,
      maxWidth: 100,
      type: 'date',
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'phoneNo',
      name: '手機號碼',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'address',
      name: (
        <span>
          戶籍地址
          <span className="text-red">*</span>
        </span>
      ),
      minWidth: 200,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'contactAddress',
      name: (
        <span>
          通訊地址
          <span className="text-red">*</span>
        </span>
      ),
      minWidth: 200,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'careerSeniority',
      type: 'number',
      name: (
        <span>
          工作年資
          <span className="text-red">*</span>
          <InfoTip
            info={
              <section>
                <P>需為數字，限制小數點以下1位</P>
              </section>
            }
          />
        </span>
      ),
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'careerRecognizedSeniority',
      type: 'number',
      name: (
        <span>
          專業年資
          <span className="text-red">*</span>
          <InfoTip
            info={
              <section>
                <P>需為數字，限制小數點以下1位</P>
              </section>
            }
          />
        </span>
      ),
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'staffType',
      name: (
          <span>
            身份類別
            <span className="text-red">*</span>
            <EmployeeTypeInfo />
          </span>
      ),
      width: 120,
      type: 'numberOption',
      options: parameters.identityCategory.map(c => ({
        value: c.abbr,
        label: c.name,
      })),
      editor: p => (
        <SelectEditor
          isSearchable={false}
          value={p.row.staffType?.toString() || ''}
          onChange={value =>
            p.onRowChange({...p.row, staffType: Number(value)}, true)
          }
          options={parameters.identityCategory.map(c => ({
            value: c.abbr,
            label: c.name,
          }))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat
          {...props}
          displayText={
            parameters.identityCategory.find(
              g => g.abbr === props.row.staffType?.toString(),
            )?.name || ''
          }
        />
      ),
    },
    {
      key: 'jobTitle',
      name: (
        <span>
          職稱
          <span className="text-red">*</span>
        </span>
      ),
      width: 120,
      type: 'option',
      options: jobTitleData.jobTitles.map(c => ({value: c, label: c})),
      editor: p => (
        <SelectEditor
          value={p.row.jobTitle || ''}
          onChange={value => p.onRowChange({...p.row, jobTitle: value}, true)}
          options={jobTitleData.jobTitles.map(c => ({value: c, label: c}))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat {...props} displayText={props.row.jobTitle} />
      ),
    },
    {
      key: 'jobGrade',
      name: '職等',
      width: 130,
      type: 'option',
      options: jobTitleData.jobGrades.map(c => ({value: c.name, label: c.name})),
      editor: p => (
        <SelectEditor
          isClearable
          value={p.row.jobGrade || ''}
          onChange={value => p.onRowChange({...p.row, jobGrade: value, jobLevel: undefined}, true)}
          options={jobTitleData.jobGrades.map(c => ({value: c.name, label: c.name}))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat {...props} displayText={props.row.jobGrade} />
      ),
    },
    {
      key: 'jobLevel',
      name: '職級',
      width: 130,
      type: 'option',
      jobLevelResolver: (jobGrade: string) => jobTitleData.jobGrades.find(grade => grade.name === jobGrade)?.jobLevels.map(c => ({value: c, label: c})) || [],
      editor: p => {
        const jobGradeOptions = jobTitleData.jobGrades.find(g => g.name === p.row.jobGrade)
        const jobLevelOptions = jobGradeOptions ? jobGradeOptions.jobLevels : [];
        return (
          <SelectEditor
            isClearable
            value={p.row.jobLevel || ''}
            onChange={value => p.onRowChange({...p.row, jobLevel: value}, true)}
            options={jobLevelOptions.map(c => ({value: c, label: c}))}
            rowHeight={p.rowHeight}
            menuPortalTarget={p.editorPortalTarget}
          />
        )
      },
      formatter: props => (
        <SelectCellFormat {...props} displayText={props.row.jobLevel} />
      ),
    },
    {
      key: 'businessEmail',
      name: (
        <span>
          公司信箱
          <InfoTip
            info={
              <section>
                <P>僅可填寫在職人員的公司信箱</P>
                <P>【注意】於最後一步啟用專區後，該信箱即會開始收到信件。如不想讓員工收到信件，請勿填寫此欄的公司信箱。</P>
              </section>
            }
          />
        </span>
      ),
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'englishName',
      name: '英文姓名',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'maritalStatus',
      name: '婚姻狀態',
      width: 130,
      type: 'numberOption',
      options: parameters?.maritalStatus.map(c => ({value: c.abbr, label: c.name})),
      editor: p => (
        <SelectEditor
          isClearable
          isSearchable={false}
          value={p.row.maritalStatus?.toString() || ''}
          onChange={value => {
            if (value) {
              p.onRowChange({...p.row, maritalStatus: Number(value)}, true)
            } else {
              p.onRowChange({...p.row, maritalStatus: undefined}, true)
            }
          }}
          options={parameters?.maritalStatus.map(c => ({value: c.abbr, label: c.name}))}
          rowHeight={p.rowHeight}
          menuPortalTarget={p.editorPortalTarget}
        />
      ),
      formatter: props => (
        <SelectCellFormat
          {...props}
          displayText={
            parameters?.maritalStatus.find(
              g => g.abbr === props.row.maritalStatus?.toString(),
            )?.name || ''
          }
        />
      ),
    },
    {
      key: 'landlineNumber',
      name: '住家電話',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'extensionNumber',
      name: '分機',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'businessMobileNumber',
      name: '公務手機',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'personalEmail',
      name: '私人信箱',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'emergency',
      name: '緊急聯絡人',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'emergencyRelationship',
      name: '緊急聯絡人關係',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
    {
      key: 'emergencyMobileNumber',
      name: '緊急聯絡人手機',
      width: 130,
      editor: (props) => <div data-test-id={`${props.rowIdx}-${props.column.key}`} className="contents"><TextEditor {...props}/></div>,
      formatter: props => <CellFormat {...props} />,
    },
  ];
  return ColumnConfig;
}
